import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Lock, Inbox, Loader2, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import { InquiryDetails } from "@/components/InquiryDetails";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("wb_admin_token") || "");
  const [passcode, setPasscode] = useState("");
  const [checking, setChecking] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadInquiries = useCallback(async (authToken, signal) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        headers: { Authorization: `Bearer ${authToken}` },
        signal,
      });
      setInquiries(data);
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (err.response?.status === 401) {
        localStorage.removeItem("wb_admin_token");
        setToken("");
        toast.error("Session expired. Sign in again.");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    if (token) loadInquiries(token, controller.signal);
    return () => controller.abort();
  }, [token, loadInquiries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        passcode,
      });
      localStorage.setItem("wb_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-void text-white font-body">
      <Toaster position="bottom-right" theme="dark" />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between">
          <Logo className="h-9 w-auto" alt="Web Builders" />
          <a href="/" data-testid="admin-back-to-site-link" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 hover:text-gold transition-colors">
            Back to site
          </a>
        </div>

        {!token ? (
          <div className="mt-20 max-w-md mx-auto">
            <div className="rounded-xl border border-white/10 bg-surface p-10">
              <div className="h-12 w-12 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-gold" aria-hidden="true" />
              </div>
              <h1 className="mt-6 font-display font-medium text-2xl">Owners inbox</h1>
              <p className="mt-2 text-sm text-white/55">Enter the passcode to read project inquiries.</p>
              <form onSubmit={handleLogin} className="mt-8 space-y-4">
                <input
                  data-testid="admin-passcode-input"
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Passcode"
                  className="w-full rounded-sm bg-transparent border border-white/20 focus:border-gold focus:outline-none px-4 py-3 text-sm placeholder:text-white/35"
                />
                <button
                  data-testid="admin-login-button"
                  type="submit"
                  disabled={checking}
                  className="w-full rounded-sm bg-brand hover:bg-brand-bright disabled:opacity-60 text-white font-medium px-6 py-3 text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  {checking ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : "Open inbox"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h1 className="font-display font-medium text-3xl flex items-center gap-3">
                <Inbox className="h-7 w-7 text-gold" aria-hidden="true" />
                Project inquiries
              </h1>
              <button
                data-testid="admin-refresh-button"
                onClick={() => loadInquiries(token)}
                className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/50 hover:text-gold transition-colors cursor-pointer"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
                Refresh
              </button>
            </div>

            {loading && inquiries.length === 0 ? (
              <p className="mt-16 text-center text-white/40">Loading inquiries...</p>
            ) : inquiries.length === 0 ? (
              <div className="mt-16 rounded-xl border border-dashed border-white/15 py-20 text-center">
                <p className="font-display text-xl text-white/60">No inquiries yet.</p>
                <p className="mt-2 text-sm text-white/40">New submissions from the contact form land here.</p>
              </div>
            ) : (
              <ul className="mt-10 space-y-4" data-testid="admin-inquiry-list">
                {inquiries.map((inquiry) => (
                  <li
                    key={inquiry.id}
                    data-testid={`admin-inquiry-card-${inquiry.id}`}
                    className="rounded-lg border border-white/10 bg-surface p-6 hover:border-white/25 transition-colors"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="h-2 w-2 rounded-full bg-brand" aria-hidden="true" />
                        <h2 className="font-display font-medium text-xl">{inquiry.name}</h2>
                        {inquiry.business && (
                          <span className="font-mono text-xs text-white/40">· {inquiry.business}</span>
                        )}
                      </div>
                      <span className="font-mono text-xs text-white/40">
                        {new Date(inquiry.created_at).toLocaleString()}
                      </span>
                    </div>
                    <a
                      data-testid={`admin-inquiry-email-${inquiry.id}`}
                      href={`mailto:${inquiry.email}`}
                      className="mt-2 inline-block text-sm text-gold hover:underline break-all"
                    >
                      {inquiry.email}
                    </a>
                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/50">
                        Pages: <span className="text-gold">{inquiry.pages}</span>
                      </span>
                      {inquiry.features?.length > 0 && (
                        <span className="flex flex-wrap gap-2">
                          {inquiry.features.map((feature) => (
                            <span key={feature} className="rounded-full border border-white/15 px-3 py-1 text-xs">
                              {feature}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                    {inquiry.message && (
                      <p className="mt-4 text-sm text-white/60 leading-relaxed border-l-2 border-white/15 pl-4">
                        {inquiry.message}
                      </p>
                    )}
                    <InquiryDetails inquiry={inquiry} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
